import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Shared layout for the map's device popup: titled sections, a two-column
 * button grid (every button the same width and style), and aligned
 * label/value rows.
 */

// One style for every button in the popup.
export const popupButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.75rem',
  py: 0.5,
  px: 1,
  minWidth: 0,
  borderWidth: 1.5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': { borderWidth: 1.5 },
};

// Fixed width for a button next to a text field (Save / Set).
export const popupInlineButtonSx = { ...popupButtonSx, width: 72, flexShrink: 0 };

/** A titled block with a divider above it. */
export function PopupSection({ title, icon = null, action = null, children }) {
  return (
    // Fixed colour: Google's popup is white even in dark mode.
    <Box sx={{ pt: 1.25, borderTop: '1px solid #e0e0e0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, minHeight: 20 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary', flex: 1 }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** Two equal columns of buttons; children should be full-width buttons. */
export function PopupButtonGrid({ children, sx }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75, ...sx }}>
      {children}
    </Box>
  );
}

/** Aligned label / value rows. rows: [label, valueNode] (falsy rows skipped). */
export function PopupInfoRows({ rows }) {
  const visible = rows.filter(Boolean);
  if (visible.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(0, 1fr)',
        columnGap: 1.5,
        rowGap: 0.5,
        alignItems: 'baseline',
      }}
    >
      {visible.map(([label, value]) => (
        <React.Fragment key={label}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
          <Box sx={{ minWidth: 0, typography: 'body2', fontWeight: 500, wordBreak: 'break-word' }}>
            {value}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}
